import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  ApiError,
  ApiErrorClass,
  PlanFrequency,
  Profile,
} from "@readyfit-common/models";
import { handleApiError, handleApiResponse } from "@readyfit-common/utils";
import ConfirmationModal from "src/grading/components/ConfirmationModal/ConfirmationModal";
import ProfileQuestions, {
  Question,
} from "src/grading/components/ProfileQuestions/ProfileQuestions";
import { RFSwitch } from "src/grading/components/RFSwitch/RFSwitch";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { useUpdateProfileMutation } from "src/store/reducers/auth/authApiSlice";
import { selectCurrentUser } from "src/store/reducers/auth/authSlice";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import theme from "src/theme";
import PasswordChangeModal from "./PasswordChangeModal/PasswordChangeModal";
import useStyles from "./Profile.styles";

const DAYS = [
  {
    label: "Sun",
    value: "weekly-sun",
  },
  {
    label: "Mon",
    value: "weekly-mon",
  },
  {
    label: "Tue",
    value: "weekly-tue",
  },
  {
    label: "Wed",
    value: "weekly-wed",
  },
  {
    label: "Thu",
    value: "weekly-thu",
  },
  {
    label: "Fri",
    value: "weekly-fri",
  },
  {
    label: "Sat",
    value: "weekly-sat",
  },
];

const ProfileInfoRow = ({
  label,
  subLabel,
  children,
}: {
  label: string;
  subLabel?: string;
  children: React.ReactNode;
}) => {
  const classes = useStyles({ isEditing: false })();

  return (
    <Grid container className={classes.profileRow} spacing={1}>
      <Grid item xs={6}>
        <Grid container flexDirection={"column"}>
          <Typography variant="caption">{label}</Typography>
          {subLabel && (
            <Typography variant="caption" color={"#666"} fontSize={"14px"}>
              {subLabel}
            </Typography>
          )}
        </Grid>
      </Grid>
      <Grid container item xs={6} justifyContent={"flex-end"}>
        {children}
      </Grid>
    </Grid>
  );
};

interface SettingsTabProps {
  isEditing: boolean;
}

const SettingsTab = ({ isEditing }: SettingsTabProps) => {
  const classes = useStyles({ isEditing })();
  const profile: Profile = useAppSelector(selectCurrentUser);
  const [questions, setQuestions] = useState<
    { question: string; newQuestion?: boolean }[]
  >([]);
  const [feedbackQuestions, setFeedbackQuestions] = useState<
    { question: string; newQuestion?: boolean }[]
  >([]);
  const [collectFeedback, setCollectFeedback] = useState(
    profile?.coachInfo?.collectFeedback || false,
  );
  const [updateProfile] = useUpdateProfileMutation();
  const dispatch = useAppDispatch();
  const [showDeleteQuestionModal, setShowDeleteQuestionModal] = useState(false);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<{
    index: number;
    isFeedbackQuestion?: boolean;
  } | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [frequency, setFrequency] = useState<PlanFrequency | null>(
    profile?.coachInfo?.feedbackFrequency || null,
  );
  const [measurement, setMeasurement] = useState<"miles" | "kilometers">(
    profile?.defaultMeasurementUnit || "miles",
  );
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);

  useEffect(() => {
    if (profile?.coachInfo?.customQuestions) {
      setQuestions(
        profile.coachInfo.customQuestions.map((question) => ({
          question,
        })),
      );
    }
    if (profile?.coachInfo?.feedbackQuestions) {
      setFeedbackQuestions(
        profile.coachInfo.feedbackQuestions.map((question) => ({
          question,
        })),
      );
    }
  }, [profile]);

  const handleQuestionsChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
    isFeedbackQuestion?: boolean,
  ) => {
    const value = event.target.value;
    const newQuestions = isFeedbackQuestion
      ? [...feedbackQuestions]
      : [...questions];
    newQuestions[index] = { ...newQuestions[index], question: value };
    if (isFeedbackQuestion) {
      setFeedbackQuestions(newQuestions);
    } else {
      setQuestions(newQuestions);
    }
  };

  const handleAddQuestion = (isFeedbackQuestion?: boolean) => {
    console.log("isFeedbackQuestion", isFeedbackQuestion);
    if (isFeedbackQuestion) {
      setFeedbackQuestions([
        ...feedbackQuestions,
        { question: "", newQuestion: true },
      ]);
    } else {
      setQuestions([...questions, { question: "", newQuestion: true }]);
    }
  };

  const handleOpenDeleteQuestionModal = (
    index: number,
    isFeedbackQuestion?: boolean,
  ) => {
    setSelectedQuestionIndex({ index, isFeedbackQuestion });
    setShowDeleteQuestionModal(true);
  };

  const handleDeleteQuestion = () => {
    try {
      if (!selectedQuestionIndex)
        throw new ApiErrorClass({ message: "No question selected" });
      const newQuestions = selectedQuestionIndex?.isFeedbackQuestion
        ? [...feedbackQuestions]
        : [...questions];
      newQuestions.splice(selectedQuestionIndex?.index || 0, 1);

      const questionsKey = selectedQuestionIndex?.isFeedbackQuestion
        ? "feedbackQuestions"
        : "customQuestions";

      updateProfile({
        coachInfo: {
          [questionsKey]: newQuestions.map((question) => question.question),
        },
      }).unwrap();

      handleCloseDeleteQuestionModal();
      dispatch(
        showSnackbar({
          open: true,
          message: "Question deleted successfully",
          type: "success",
        }),
      );
    } catch (error) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  const handleCloseDeleteQuestionModal = () => {
    setShowDeleteQuestionModal(false);
    setSelectedQuestionIndex(null);
  };

  const handleSubmitQuestions = async (
    isFeedbackQuestion?: boolean,
    newQuestionsOrder?: Question[],
  ) => {
    try {
      let newQuestions = [];
      if (newQuestionsOrder) {
        newQuestions = [...newQuestionsOrder];
      } else {
        newQuestions = isFeedbackQuestion
          ? [...feedbackQuestions.filter((q) => q.question)]
          : [...questions.filter((q) => q.question)];
      }

      // if (newQuestionsOrder) {
      //   newQuestionsOrder.forEach((question) => {
      //     if (question.newQuestion) {
      //       newQuestions.push(question);
      //     }
      //   });
      // }

      const questionsKey = isFeedbackQuestion
        ? "feedbackQuestions"
        : "customQuestions";

      await updateProfile({
        coachInfo: {
          [questionsKey]: newQuestions.map((question) => question.question),
        },
      }).unwrap();
    } catch (error) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  const handleCollectFeedbackChange = (checked: boolean) => {
    setCollectFeedback(checked);
    updateProfileSettings({
      coachInfo: {
        collectFeedback: checked,
      },
    });
  };

  const handleFrequencyClose = () => {
    setAnchorEl(null);
  };

  const handleFrequencyDayClick = (type: PlanFrequency) => {
    setFrequency(type);
    handleFrequencyClose();

    updateProfileSettings({
      coachInfo: {
        feedbackFrequency: type,
      },
    });
  };

  const handleFrequencyChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    console.log("event", event.target.value);
    if (event.target.value === "weekly") {
      setAnchorEl(event.currentTarget);
      return;
    }
    setFrequency(event.target.value as PlanFrequency);

    updateProfileSettings({
      coachInfo: {
        feedbackFrequency: event.target.value as PlanFrequency,
      },
    });
  };

  const updateProfileSettings = async (
    profile: Partial<
      Pick<
        Profile,
        "firstName" | "lastName" | "coachInfo" | "defaultMeasurementUnit"
      >
    >,
  ) => {
    try {
      const response = await updateProfile(profile).unwrap();
      handleApiResponse(dispatch, response);
    } catch (error) {
      const typedError = error as ApiError;
      handleApiError(dispatch, typedError);
    }
  };

  // const handleMeasurementChange = async (
  //   event: React.ChangeEvent<HTMLInputElement>,
  // ) => {
  //   try {
  //     setMeasurement(event.target.value as "miles" | "kilometers");
  //     await updateProfileSettings({
  //       defaultMeasurementUnit: event.target.value as "miles" | "kilometers",
  //     });
  //   } catch (error) {
  //     const typedError = error as ApiError;
  //     handleApiError(dispatch, typedError);
  //   }
  // };

  return (
    <Grid container className={classes.contentContainer}>
      <Grid container alignItems={"center"}>
        <Typography variant="h4" fontWeight={"bold"}>
          Settings
        </Typography>
      </Grid>

      <Grid container className={classes.profileColumnContainer} spacing={5}>
        {/* // FIRST COLUMN */}
        <Grid item xs={12} md={6}>
          <ProfileInfoRow label="Measurement">
            <FormControl>
              <RadioGroup
                row
                name="measurement"
                onChange={(e) => {
                  const value = e.target.value as "miles" | "kilometers";
                  setMeasurement(value);
                  updateProfileSettings({
                    defaultMeasurementUnit: value,
                  });
                }}
                value={measurement}
              >
                <FormControlLabel
                  value="kilometers"
                  control={<Radio />}
                  label="Kilometers"
                />
                <FormControlLabel
                  value="miles"
                  control={<Radio />}
                  label="Miles"
                />
              </RadioGroup>
            </FormControl>
          </ProfileInfoRow>

          <ProfileInfoRow label={"Password"}>
            <Button
              variant="contained"
              style={{ borderRadius: "8px" }}
              sx={{ backgroundColor: "#000", width: theme.spacing(21) }}
              onClick={() => setShowPasswordChangeModal(true)}
            >
              Change
            </Button>
          </ProfileInfoRow>

          <ProfileInfoRow
            label={"Customize onboarding"}
            subLabel={"Create your own intake questions for a new athlete"}
          >
            <ProfileQuestions
              questions={questions}
              onChange={handleQuestionsChange}
              onSubmit={handleSubmitQuestions}
              onDelete={handleOpenDeleteQuestionModal}
              onAdd={handleAddQuestion}
              setQuestions={setQuestions}
            />
          </ProfileInfoRow>
        </Grid>
        {/* // SECOND COLUMN */}
        <Grid item xs={12} md={6}>
          <ProfileInfoRow
            label={"Customize Athlete Communication"}
            subLabel={
              "ReadyFit sends athletes a text message on workout days tocollect consistent feedback. Athletes don’t get a text if they post a Strava workout or enter information on the app."
            }
          >
            <FormControlLabel
              control={
                <RFSwitch
                  onChange={(event, checked) =>
                    handleCollectFeedbackChange(checked)
                  }
                  checked={collectFeedback}
                  activeColor={theme.palette.primary.main}
                  inactiveColor={theme.palette.primary.contrastText}
                  inactiveTrackColor={"gray"}
                />
              }
              label={"Alternate Exercise"}
              labelPlacement="end"
              className={classes.checkbox}
            />
          </ProfileInfoRow>

          <ProfileInfoRow
            label={"Athlete Feedback"}
            subLabel={"Customize the questions that athletes answer."}
          >
            <ProfileQuestions
              questions={feedbackQuestions}
              onChange={handleQuestionsChange}
              onSubmit={handleSubmitQuestions}
              onDelete={handleOpenDeleteQuestionModal}
              onAdd={handleAddQuestion}
              setQuestions={setFeedbackQuestions}
              isFeedbackQuestion
            />
          </ProfileInfoRow>

          <ProfileInfoRow
            label={"Frequency"}
            subLabel={"Edit when texts are sent to athletes."}
          >
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                sx={{ justifyContent: "flex-end" }}
                onChange={handleFrequencyChange}
                value={frequency}
              >
                <FormControlLabel
                  value="daily"
                  control={<Radio className={classes.radio} />}
                  label="Daily"
                />
                <FormControlLabel
                  value="weekly"
                  control={<Radio className={classes.radio} />}
                  label="Weekly"
                  checked={frequency?.includes("weekly")}
                  onClick={(event) => {
                    event.stopPropagation();
                    setAnchorEl(event.currentTarget);
                  }}
                />
                <Menu
                  id="weekly-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleFrequencyClose}
                  MenuListProps={{
                    "aria-labelledby": "weekly-button",
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        borderRadius: theme.spacing(1),
                      },
                    },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    textAlign={"center"}
                    style={{
                      paddingTop: theme.spacing(1),
                      paddingBottom: theme.spacing(1),
                    }}
                  >
                    Select Day of the week
                  </Typography>

                  <Divider
                    style={{
                      marginTop: theme.spacing(0.5),
                      marginBottom: theme.spacing(0.5),
                    }}
                  />
                  <Grid container flexDirection={"row"}>
                    {DAYS.map((day) => (
                      <MenuItem
                        key={day.value}
                        onClick={() =>
                          handleFrequencyDayClick(day.value as PlanFrequency)
                        }
                        value={day.value}
                        sx={
                          frequency === day.value
                            ? {
                                backgroundColor: "#C5D9CF",
                                borderRadius: theme.spacing(1),
                              }
                            : {
                                "&:hover": {
                                  borderRadius: theme.spacing(1),
                                },
                              }
                        }
                      >
                        <Typography variant="button">{day.label}</Typography>
                      </MenuItem>
                    ))}
                  </Grid>
                </Menu>
                <FormControlLabel
                  value="key-workouts"
                  control={<Radio className={classes.radio} />}
                  label="Key Workouts"
                />
              </RadioGroup>
            </FormControl>
          </ProfileInfoRow>
        </Grid>
      </Grid>
      <ConfirmationModal
        open={showDeleteQuestionModal}
        onClose={handleCloseDeleteQuestionModal}
        onConfirm={() => handleDeleteQuestion()}
        title="Delete question"
        message="This action cannot be undone. Are you sure you want to remove the question?"
        confirmationButtonTitle="Delete Question"
      />
      <PasswordChangeModal
        open={showPasswordChangeModal}
        onClose={() => {
          setShowPasswordChangeModal(false);
        }}
        title="Change Password"
        message="Enter your current password and new password to change your password."
        confirmationButtonTitle="Change Password"
      />
    </Grid>
  );
};

export default SettingsTab;
